// Zod validators for every shared entity, plus the RunResult schema
// (re-exported from ../schemas/run-result.ts).
//
// These schemas intentionally mirror the TypeScript interfaces in ../types/*
// field-for-field. Keep both in sync when either changes. Nested
// free-form/JSON blobs (config snapshots, context snapshots, tool params)
// are validated as `z.unknown()`/`z.record(z.unknown())` here — tightening
// those is business logic for a later task, not part of this scaffold.

import { z } from 'zod'

import {
  ApprovalStatus,
  ApprovalType,
  AuditEventType,
  ExecutorType,
  HealthStatus,
  McpTransportType,
  RiskLevel,
  RunStatus,
  SandboxProfileType,
  TaskSource,
  ToolClassification,
  UserRoleName,
} from '../constants/index.js'

export {
  RunResultSchema,
  RunResultStatusSchema,
  TestSummarySchema,
  ArtifactRefSchema,
  PullRequestRefSchema,
  parseRunResult,
  safeParseRunResult,
} from '../schemas/run-result.js'
export type { RunResult, RunResultStatus } from '../schemas/run-result.js'

// Per-role run settings (FD-adjacent; see docs/plans/2026-09-30-runners/
// 05-web-en-rolinstellingen.md §2.1) — kept in its own file since it's
// actively built on by several in-flight plans, not the entity scaffold.
export * from './run-settings.js'

import { PROJECT_KEY_RE } from '../boards/defaults.js'
import { SPEC_MODES } from '../types/project.js'
import { RunResultSchema } from '../schemas/run-result.js'
import { RunSettingsOverrideSchema } from './run-settings.js'
import type { RunResult } from '../schemas/run-result.js'
import { DECISION_OPTION_VALUE_RE, WORKFLOW_VARIABLE_NAME_RE, WORKFLOW_VARIABLE_VALUE_MAX } from '../workflows/variables.js'
import {
  DEFAULT_WORKFLOW_REF,
  MAX_SUBTASKS_PARALLEL,
  MODEL_TIERS,
  WORKFLOW_INBOX_STATE,
  workflowStateTargets,
  type ProjectWorkflows,
  type WorkflowDefinition,
  type WorkflowState,
  type WorkflowValidationResult,
} from '../types/workflow.js'

// ---------------------------------------------------------------------------
// Shared value schemas
// ---------------------------------------------------------------------------

export const IsoDateTimeSchema = z.string().datetime({ offset: true }).or(z.string())

export const BudgetLimitsSchema = z.object({
  maxCostCents: z.number().int().nonnegative().optional(),
  maxModelTokens: z.number().int().nonnegative().optional(),
  maxDurationMs: z.number().int().nonnegative().optional(),
  maxToolCalls: z.number().int().nonnegative().optional(),
  maxRetries: z.number().int().nonnegative().optional(),
  maxConcurrentRuns: z.number().int().nonnegative().optional(),
  maxArtifactStorageBytes: z.number().int().nonnegative().optional(),
  maxArtifactSizeBytes: z.number().int().nonnegative().optional(),
})

export const RetentionPolicySchema = z.object({
  runRetentionDays: z.number().int().positive().optional(),
  auditLogRetentionDays: z.number().int().positive().optional(),
  artifactRetentionDays: z.number().int().positive().optional(),
})

export const NetworkPolicySchema = z.object({
  defaultDenyAll: z.boolean(),
  allowedDomains: z.array(z.string()),
  allowedCidrs: z.array(z.string()).optional(),
})

export const ApprovalPolicySchema = z.object({
  requireApprovalForPrivilegedTools: z.boolean(),
  requireApprovalForDestructiveTools: z.boolean(),
  requireApprovalForBranchPush: z.boolean(),
  requireApprovalForPullRequest: z.boolean(),
  requireApprovalForDeploymentMergeRelease: z.boolean(),
  defaultExpiryMs: z.number().int().positive().optional(),
})

// ---------------------------------------------------------------------------
// Workspace
// ---------------------------------------------------------------------------

export const WorkspaceSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z.string().nullable(),
  defaultTimezone: z.string(),
  defaultBudgets: BudgetLimitsSchema,
  retentionPolicy: RetentionPolicySchema,
  allowedExecutorTypes: z.array(z.nativeEnum(ExecutorType)),
  defaultNetworkPolicy: NetworkPolicySchema,
  defaultApprovalPolicy: ApprovalPolicySchema,
  /** Whether external tools may reach Goblic's MCP server with an API token. */
  externalApiAccess: z.boolean().optional(),
  /** How a subscription-login run's notional cost is shown ("Run costs" workspace setting). */
  runCostDisplay: z.enum(['estimate', 'included']).optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

export const WorkspaceSummarySchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z.string().nullable(),
  roles: z.array(z.nativeEnum(UserRoleName)),
  createdAt: IsoDateTimeSchema,
})

/** Body of POST /workspaces (`CreateWorkspaceBody`). */
export const CreateWorkspaceBodySchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(2000).nullable().optional(),
  timezone: z.string().min(1).optional(),
})
export type CreateWorkspaceBody = z.infer<typeof CreateWorkspaceBodySchema>

/** Body of POST /auth/switch-workspace (`SwitchWorkspaceBody`). */
export const SwitchWorkspaceBodySchema = z.object({
  workspaceId: z.string().uuid(),
})
export type SwitchWorkspaceBody = z.infer<typeof SwitchWorkspaceBodySchema>

// ---------------------------------------------------------------------------
// Workspace invitations, leave and delete
// ---------------------------------------------------------------------------

/** An open invitation (`GET /workspaces/:id/invitations`). Never carries the token. */
export const InvitationSummarySchema = z.object({
  id: z.string(),
  email: z.string().email(),
  roles: z.array(z.nativeEnum(UserRoleName)),
  invitedBy: z.object({ id: z.string(), displayName: z.string() }).nullable(),
  createdAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
})
export type InvitationSummary = z.infer<typeof InvitationSummarySchema>

/** Body of POST /workspaces/:id/invitations. */
export const CreateInvitationBodySchema = z.object({
  email: z.string().trim().email(),
  roles: z.array(z.nativeEnum(UserRoleName)).min(1),
  /** E-mail the link too. Default: yes when the server has mail configured. */
  sendEmail: z.boolean().optional(),
})
export type CreateInvitationBody = z.infer<typeof CreateInvitationBodySchema>

/**
 * Response of POST /workspaces/:id/invitations and of .../:invitationId/resend:
 * the only place the link (token) appears. `emailSent` says whether the link
 * went out by e-mail; `emailError` (generic) is set when sending was tried and failed.
 */
export const CreateInvitationResponseSchema = z.object({
  invitation: InvitationSummarySchema,
  url: z.string(),
  emailSent: z.boolean(),
  emailError: z.string().optional(),
})
export type CreateInvitationResponse = z.infer<typeof CreateInvitationResponseSchema>

/** Response of GET /workspaces/:id/invitations/options. */
export const InvitationOptionsSchema = z.object({ mailEnabled: z.boolean() })
export type InvitationOptions = z.infer<typeof InvitationOptionsSchema>

/**
 * Response of the public GET /auth/invitations/:token. `account` says what the
 * invitee has to do: `none` = create an account, `password` = sign in,
 * `no-password` = an account without a password exists; set one.
 */
export const InvitationPreviewSchema = z.object({
  workspaceName: z.string(),
  email: z.string().email(),
  roles: z.array(z.nativeEnum(UserRoleName)),
  invitedByName: z.string().nullable(),
  expiresAt: IsoDateTimeSchema,
  account: z.enum(['none', 'password', 'no-password']),
})
export type InvitationPreview = z.infer<typeof InvitationPreviewSchema>

/**
 * Body of the public POST /auth/invitations/:token/accept. `displayName` is
 * required when `account` is `none`, optional for `no-password`, ignored for
 * `password`. The password is at least 8 characters (same rule as setup) when
 * creating or setting one.
 */
export const AcceptInvitationBodySchema = z.object({
  password: z.string().min(1),
  displayName: z.string().trim().min(1).max(80).optional(),
})
export type AcceptInvitationBody = z.infer<typeof AcceptInvitationBodySchema>

/** Body of DELETE /workspaces/:id: must equal the workspace name exactly. */
export const DeleteWorkspaceBodySchema = z.object({
  confirmName: z.string(),
})
export type DeleteWorkspaceBody = z.infer<typeof DeleteWorkspaceBodySchema>

// ---------------------------------------------------------------------------
// User / membership
// ---------------------------------------------------------------------------

export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().min(1),
  passwordHash: z.string().nullable().optional(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

export const ApprovalScopeSchema = z.object({
  projectIds: z.array(z.string()),
  approvalTypes: z.array(z.string()),
})

export const WorkspaceMembershipSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  userId: z.string(),
  roles: z.array(z.nativeEnum(UserRoleName)),
  approvalScope: ApprovalScopeSchema.optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Role
// ---------------------------------------------------------------------------

export const RoleSkillBindingSchema = z.object({
  skillId: z.string(),
  skillVersion: z.string(),
  enabled: z.boolean(),
})

export const RoleToolPermissionSchema = z.object({
  mcpServiceId: z.string(),
  toolName: z.string(),
  allowed: z.boolean(),
  requiresApproval: z.boolean(),
  allowedParams: z.record(z.unknown()).optional(),
})

export const OutputContractSchema = z.object({
  requiresRunResult: z.boolean(),
  requiredFields: z.array(z.string()),
})

export const RoleConfigSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  baseInstruction: z.string(),
  defaultExecutorId: z.string(),
  defaultModel: z.string(),
  skills: z.array(RoleSkillBindingSchema),
  toolPermissions: z.array(RoleToolPermissionSchema),
  memoryCollectionIds: z.array(z.string()),
  defaultSandboxProfileId: z.string(),
  resourceLimits: BudgetLimitsSchema,
  outputContract: OutputContractSchema,
  allowedRiskLevel: z.nativeEnum(RiskLevel),
  actionsRequiringApproval: z.array(z.string()),
})

export const RoleVersionSchema = RoleConfigSchema.extend({
  id: z.string(),
  roleId: z.string(),
  version: z.number().int().positive(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
  createdBy: z.string(),
})

export const RoleSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string().min(1),
  description: z.string().nullable(),
  currentVersionId: z.string(),
  currentVersion: z.number().int().positive(),
  isDeactivated: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Skill
// ---------------------------------------------------------------------------

export const SkillManifestSchema = z.object({
  name: z.string().min(1),
  version: z.string(),
  description: z.string(),
  permissions: z.array(z.string()),
  compatibleRoles: z.array(z.string()),
})

export const SkillFileRefSchema = z.object({
  path: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  checksum: z.string(),
})

export const SkillVersionSchema = z.object({
  id: z.string(),
  skillId: z.string(),
  version: z.string(),
  manifest: SkillManifestSchema,
  instructions: z.string(),
  scripts: z.array(SkillFileRefSchema),
  templates: z.array(SkillFileRefSchema),
  references: z.array(SkillFileRefSchema),
  checksum: z.string(),
  estimatedContextTokens: z.number().int().nonnegative(),
  approvalStatus: z.enum(['pending_review', 'approved', 'blocked']),
  containsExecutableCode: z.boolean(),
  approvedBy: z.string().nullable().optional(),
  approvedAt: IsoDateTimeSchema.nullable().optional(),
  createdAt: IsoDateTimeSchema,
})

export const SkillSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string().min(1),
  sourceKind: z.enum(['folder', 'archive', 'git_reference', 'registry', 'authored']),
  sourceRef: z.string(),
  currentVersionId: z.string(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// MCP service
// ---------------------------------------------------------------------------

export const McpToolDefinitionSchema = z.object({
  name: z.string(),
  description: z.string().nullable(),
  classification: z.string(),
  lastSeenAt: IsoDateTimeSchema,
})

export const McpAuthConfigSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('header'), header: z.string().min(1), prefix: z.string() }),
  z.object({ kind: z.literal('env'), name: z.string().min(1) }),
])

export const McpToolRuleSchema = z.object({
  toolName: z.string(),
  allowed: z.boolean(),
})

export const McpServiceSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string().min(1),
  description: z.string().nullable(),
  transportType: z.nativeEnum(McpTransportType),
  endpointOrCommand: z.string(),
  auth: McpAuthConfigSchema.nullable(),
  hasSecret: z.boolean(),
  healthStatus: z.nativeEnum(HealthStatus),
  lastHealthCheckAt: IsoDateTimeSchema.nullable(),
  lastHealthError: z.string().nullable(),
  availableTools: z.array(McpToolDefinitionSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
})

export const McpCallLogEntrySchema = z.object({
  id: z.string(),
  runId: z.string(),
  agentId: z.string(),
  roleId: z.string(),
  mcpServiceId: z.string(),
  toolName: z.string(),
  startedAt: IsoDateTimeSchema,
  endedAt: IsoDateTimeSchema.nullable(),
  resultStatus: z.enum(['success', 'error', 'denied', 'timeout']),
  redactedParams: z.unknown(),
  redactedOutput: z.unknown(),
  costCents: z.number().nonnegative().nullable(),
  approvalId: z.string().nullable(),
})

// ---------------------------------------------------------------------------
// Workflows (types/workflow.ts)
// ---------------------------------------------------------------------------

/** Workflow keys are slugs: lowercase letters, digits, '-' and '_'. */
export const WorkflowKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, 'Use lowercase letters, digits, "-" or "_"')

const workflowStateRefSchema = z.string().min(1).nullable().optional()

export const WorkflowDecisionOptionSchema = z.object({
  value: z.string().regex(DECISION_OPTION_VALUE_RE, 'Use 1-32 lowercase letters, digits, "-" or "_"'),
  description: z.string().max(1000).nullable().optional(),
  next: workflowStateRefSchema,
  nextWorkflow: z
    .object({ workflow: z.string().min(1), state: workflowStateRefSchema })
    .nullable()
    .optional(),
  set: z
    .object({
      modelTier: z.enum(['light', 'standard', 'heavy']).nullable().optional(),
      vars: z.record(z.string(), z.string()).nullable().optional(),
    })
    .nullable()
    .optional(),
})

export const WorkflowStateSchema = z.object({
  name: z.string().trim().min(1).max(100),
  kind: z.enum(['agent', 'human', 'terminal', 'decision', 'subtasks']),
  agentRole: z.string().trim().min(1).nullable().optional(),
  instructions: z.string().trim().max(8000).nullable().optional(),
  requires: z.array(z.string().min(1)).default([]),
  blocks: z.array(z.string().min(1)).default([]),
  freshContext: z.boolean().optional(),
  onSuccess: workflowStateRefSchema,
  onFailure: workflowStateRefSchema,
  onBlocked: workflowStateRefSchema,
  onApprove: workflowStateRefSchema,
  onReject: workflowStateRefSchema,
  maxRounds: z.number().int().positive().max(100).nullable().optional(),
  outcome: z.enum(['success', 'failure', 'cancelled']).optional(),
  useVariables: z.boolean().nullable().optional(),
  question: z.string().max(4000).nullable().optional(),
  variable: z.string().nullable().optional(),
  options: z.array(WorkflowDecisionOptionSchema).nullable().optional(),
  defaultOption: z.string().nullable().optional(),
  childWorkflow: z.string().trim().min(1).nullable().optional(),
  maxParallel: z.number().int().min(1).max(MAX_SUBTASKS_PARALLEL).nullable().optional(),
  ui: z.object({ x: z.number(), y: z.number() }).nullable().optional(),
})

export const WorkflowDefinitionSchema = z.object({
  key: WorkflowKeySchema,
  name: z.string().trim().min(1).max(100),
  description: z.string().nullable().optional(),
  initialState: z.string().min(1),
  states: z.array(WorkflowStateSchema).min(1),
  templateKey: z.string().nullable().optional(),
})

export const ProjectWorkflowsSchema = z.object({
  defaultWorkflow: z.string().nullable(),
  workflows: z.array(WorkflowDefinitionSchema),
})

function validateDecisionState(
  state: WorkflowState,
  byName: Map<string, WorkflowState>,
  errors: string[],
  warnings: string[],
  decisionVariables: Map<string, string>,
): void {
  const label = `Decision state "${state.name}"`
  const variable = state.variable ?? ''
  if (!variable) {
    errors.push(`${label} needs a variable.`)
  } else if (!WORKFLOW_VARIABLE_NAME_RE.test(variable)) {
    errors.push(`${label}: variable "${variable}" must match ${WORKFLOW_VARIABLE_NAME_RE.source}.`)
  } else {
    const other = decisionVariables.get(variable)
    if (other) {
      warnings.push(`Decision states "${other}" and "${state.name}" both write variable "${variable}"; the later answer overwrites the earlier one.`)
    } else {
      decisionVariables.set(variable, state.name)
    }
  }

  if (!state.agentRole?.trim()) errors.push(`${label} needs an agent role.`)

  const question = state.question?.trim() ?? ''
  if (!question) errors.push(`${label} needs a question.`)
  else if (question.length > 4000) errors.push(`${label}: the question is longer than 4000 characters.`)

  const options = state.options ?? []
  if (options.length < 2 || options.length > 8) {
    errors.push(`${label} needs 2-8 options (has ${options.length}).`)
  }
  const values = new Set<string>()
  for (const option of options) {
    const where = `${label}, option "${option.value}"`
    if (!DECISION_OPTION_VALUE_RE.test(option.value ?? '')) {
      errors.push(`${label}: option value "${option.value}" must match ${DECISION_OPTION_VALUE_RE.source}.`)
    } else if (values.has(option.value)) {
      errors.push(`${label}: option value "${option.value}" is used more than once.`)
    }
    values.add(option.value)

    const hasNext = !!option.next
    const hasSwitch = !!option.nextWorkflow?.workflow
    if (hasNext === hasSwitch) {
      errors.push(`${where} needs exactly one of next and next_workflow.`)
    } else if (hasNext && !byName.has(option.next!)) {
      errors.push(`${where} points to unknown state "${option.next}".`)
    } else if (hasSwitch && option.nextWorkflow!.workflow !== DEFAULT_WORKFLOW_REF) {
      warnings.push(`${where} continues in workflow "${option.nextWorkflow!.workflow}", which can't be checked here.`)
    }

    const tier = option.set?.modelTier
    if (tier != null && !MODEL_TIERS.includes(tier)) {
      errors.push(`${where}: model_tier "${tier}" must be one of ${MODEL_TIERS.join(', ')}.`)
    }
    for (const [name, value] of Object.entries(option.set?.vars ?? {})) {
      if (!WORKFLOW_VARIABLE_NAME_RE.test(name)) {
        errors.push(`${where}: variable name "${name}" must match ${WORKFLOW_VARIABLE_NAME_RE.source}.`)
      }
      if (typeof value !== 'string' || value.length > WORKFLOW_VARIABLE_VALUE_MAX) {
        errors.push(`${where}: value of "${name}" must be at most ${WORKFLOW_VARIABLE_VALUE_MAX} characters.`)
      }
    }
  }

  if (!state.defaultOption) {
    errors.push(`${label} needs a default option.`)
  } else if (!values.has(state.defaultOption)) {
    errors.push(`${label}: default "${state.defaultOption}" is not one of its options.`)
  }
}

/**
 * Semantic checks on one workflow (the zod schemas above only check shape).
 * Errors make the workflow unusable; warnings are shown but do not block
 * saving. Pure and browser-safe, so the web UI and the API report the same
 * findings.
 */
export function validateWorkflowDefinition(def: WorkflowDefinition): WorkflowValidationResult {
  const errors: string[] = []
  const warnings: string[] = []
  const byName = new Map<string, WorkflowState>()

  for (const state of def.states) {
    if (byName.has(state.name)) {
      errors.push(`State "${state.name}" is defined more than once.`)
    } else {
      byName.set(state.name, state)
    }
  }

  if (!byName.has(def.initialState)) {
    errors.push(`Initial state "${def.initialState}" does not exist.`)
  }

  const refFields = ['onSuccess', 'onFailure', 'onBlocked', 'onApprove', 'onReject'] as const
  const agentOnly = ['onSuccess', 'onFailure', 'onBlocked'] as const
  const humanOnly = ['onApprove', 'onReject'] as const
  const decisionVariables = new Map<string, string>()

  for (const state of def.states) {
    if (state.kind === 'decision') {
      // A decision step only leads through its options; the agent/human/terminal fields don't apply.
      for (const field of [...refFields, 'outcome'] as const) {
        if (state[field]) errors.push(`Decision state "${state.name}" cannot have ${field}; use options instead.`)
      }
    }
    for (const field of refFields) {
      const target = state[field]
      if (target && !byName.has(target)) {
        errors.push(`State "${state.name}": ${field} points to unknown state "${target}".`)
      }
    }

    if (state.kind === 'agent') {
      if (!state.agentRole) {
        errors.push(`Agent state "${state.name}" needs an agent role.`)
      }
      if (!state.onSuccess) {
        warnings.push(`Agent state "${state.name}" has no on_success; a completed run leaves the task there.`)
      }
      for (const field of humanOnly) {
        if (state[field]) warnings.push(`Agent state "${state.name}": ${field} is ignored (human states only).`)
      }
    } else if (state.kind === 'human') {
      if (!state.onApprove) {
        errors.push(`Human state "${state.name}" needs on_approve.`)
      }
      if (state.agentRole) {
        warnings.push(`Human state "${state.name}": agent role is ignored.`)
      }
      for (const field of agentOnly) {
        if (state[field]) warnings.push(`Human state "${state.name}": ${field} is ignored (agent states only).`)
      }
    } else if (state.kind === 'decision') {
      validateDecisionState(state, byName, errors, warnings, decisionVariables)
    } else if (state.kind === 'subtasks') {
      validateSubtasksState(state, errors, warnings)
    } else {
      if (refFields.some((field) => state[field])) {
        errors.push(`Terminal state "${state.name}" cannot have outgoing transitions.`)
      }
      if (!state.outcome) {
        warnings.push(`Terminal state "${state.name}" has no outcome; it is treated as success.`)
      }
      if (state.agentRole) {
        warnings.push(`Terminal state "${state.name}": agent role is ignored.`)
      }
    }

    if (state.requires.includes(state.name)) {
      errors.push(`State "${state.name}" cannot require itself.`)
    }
    if (state.blocks.includes(state.name)) {
      errors.push(`State "${state.name}" cannot block itself.`)
    }
  }

  const terminals = def.states.filter((s) => s.kind === 'terminal')
  if (terminals.length === 0) {
    errors.push('A workflow needs at least one terminal state.')
  }

  // Reachability from the initial state (only meaningful once references resolve).
  if (byName.has(def.initialState)) {
    const reached = new Set<string>([def.initialState])
    const queue = [def.initialState]
    while (queue.length > 0) {
      const current = byName.get(queue.shift()!)
      if (!current) continue
      for (const target of workflowStateTargets(current)) {
        if (byName.has(target) && !reached.has(target)) {
          reached.add(target)
          queue.push(target)
        }
      }
    }
    for (const state of def.states) {
      if (!reached.has(state.name)) {
        warnings.push(`State "${state.name}" is unreachable from "${def.initialState}".`)
      }
    }
    if (terminals.length > 0 && !terminals.some((t) => reached.has(t.name))) {
      warnings.push('No terminal state is reachable from the initial state.')
    }
  }

  return { errors, warnings }
}

/** kind=subtasks: waits for the task's subtasks, then onSuccess / onFailure (docs/plans/2026-10-03-subtaken.md §4.2). */
function validateSubtasksState(state: WorkflowState, errors: string[], warnings: string[]): void {
  if (!state.onSuccess) {
    errors.push(`Subtasks state "${state.name}" needs on_success.`)
  }
  if (!state.onFailure) {
    warnings.push(`Subtasks state "${state.name}" has no on_failure; when a subtask does not succeed the task goes to on_success too.`)
  }
  for (const field of ['onBlocked', 'onApprove', 'onReject', 'outcome'] as const) {
    if (state[field]) errors.push(`Subtasks state "${state.name}" cannot have ${field}.`)
  }
  if (state.agentRole) {
    warnings.push(`Subtasks state "${state.name}": agent role is ignored.`)
  }
  if (state.maxParallel != null && (state.maxParallel < 1 || state.maxParallel > MAX_SUBTASKS_PARALLEL)) {
    errors.push(`Subtasks state "${state.name}": max_parallel must be between 1 and ${MAX_SUBTASKS_PARALLEL}.`)
  }
  if (state.childWorkflow) {
    warnings.push(`Subtasks state "${state.name}" uses workflow "${state.childWorkflow}" for its subtasks, which can't be checked here.`)
  }
}

/**
 * Validates every workflow of a project plus the cross-workflow rules
 * (unique keys, default workflow exists).
 * - `byWorkflow`: per-workflow findings keyed by workflow key — the
 *   `validation` field of GET /projects/:id/workflows;
 * - `general`: cross-workflow findings only;
 * - `errors`/`warnings`: everything, per-workflow entries prefixed "<key>: ".
 */
export function validateProjectWorkflows(value: ProjectWorkflows): {
  errors: string[]
  warnings: string[]
  byWorkflow: Record<string, WorkflowValidationResult>
  general: WorkflowValidationResult
} {
  const errors: string[] = []
  const warnings: string[] = []
  const general: WorkflowValidationResult = { errors: [], warnings: [] }
  const byWorkflow: Record<string, WorkflowValidationResult> = {}
  const seen = new Set<string>()

  for (const def of value.workflows) {
    if (seen.has(def.key)) {
      general.errors.push(`Workflow key "${def.key}" is used more than once.`)
      continue
    }
    seen.add(def.key)
    const result = validateWorkflowDefinition(def)
    byWorkflow[def.key] = result
    errors.push(...result.errors.map((e) => `${def.key}: ${e}`))
    warnings.push(...result.warnings.map((w) => `${def.key}: ${w}`))
  }

  // Cross-workflow targets of decision options (`next_workflow`).
  const byKey = new Map(value.workflows.map((w) => [w.key, w]))
  for (const def of value.workflows) {
    for (const state of def.states) {
      if (state.kind !== 'decision') continue
      for (const option of state.options ?? []) {
        const target = option.nextWorkflow
        if (!target?.workflow) continue
        const isDefaultRef = target.workflow === DEFAULT_WORKFLOW_REF
        const targetKey = isDefaultRef ? value.defaultWorkflow : target.workflow
        const other = targetKey ? byKey.get(targetKey) : undefined
        const where = `Decision state "${state.name}", option "${option.value}"`
        let message: string | null = null
        if (isDefaultRef && !targetKey) message = `${where} continues in the project's default workflow, but none is set.`
        else if (!other) message = `${where} continues in unknown workflow "${target.workflow}".`
        else if (target.state && target.state !== WORKFLOW_INBOX_STATE && !other.states.some((s) => s.name === target.state)) {
          message = `${where} continues in unknown state "${target.state}" of workflow "${target.workflow}".`
        }
        if (message) {
          errors.push(`${def.key}: ${message}`)
          byWorkflow[def.key]?.errors.push(message)
        }
        // The per-workflow "can't be checked here" warning is resolved now.
        const stale = `${where} continues in workflow "${target.workflow}", which can't be checked here.`
        for (const list of [byWorkflow[def.key]?.warnings, warnings]) {
          const prefixed = list === warnings ? `${def.key}: ${stale}` : stale
          const idx = list?.indexOf(prefixed) ?? -1
          if (list && idx >= 0) list.splice(idx, 1)
        }
      }
    }
  }

  // Workflows that subtasks run through (`child_workflow`): they must exist and
  // may not wait for subtasks themselves (one level deep).
  const hasSubtasksState = (def: WorkflowDefinition | undefined) => Boolean(def?.states.some((s) => s.kind === 'subtasks'))
  for (const def of value.workflows) {
    for (const state of def.states) {
      if (state.kind !== 'subtasks') continue
      const where = `Subtasks state "${state.name}"`
      let message: string | null = null
      let warning: string | null = null
      if (state.childWorkflow) {
        const child = byKey.get(state.childWorkflow)
        if (!child) message = `${where} uses unknown workflow "${state.childWorkflow}" for its subtasks.`
        else if (hasSubtasksState(child)) message = `${where} uses workflow "${state.childWorkflow}" for its subtasks, which has a subtasks state itself; subtasks can't have subtasks.`
        const stale = `${where} uses workflow "${state.childWorkflow}" for its subtasks, which can't be checked here.`
        for (const list of [byWorkflow[def.key]?.warnings, warnings]) {
          const prefixed = list === warnings ? `${def.key}: ${stale}` : stale
          const idx = list?.indexOf(prefixed) ?? -1
          if (list && idx >= 0) list.splice(idx, 1)
        }
      } else if (value.defaultWorkflow && hasSubtasksState(byKey.get(value.defaultWorkflow))) {
        warning = `${where} has no child_workflow and the default workflow "${value.defaultWorkflow}" has a subtasks state; subtasks that don't name a workflow will be blocked.`
      }
      if (message) {
        errors.push(`${def.key}: ${message}`)
        byWorkflow[def.key]?.errors.push(message)
      }
      if (warning) {
        warnings.push(`${def.key}: ${warning}`)
        byWorkflow[def.key]?.warnings.push(warning)
      }
    }
  }

  if (value.defaultWorkflow !== null && !seen.has(value.defaultWorkflow)) {
    general.errors.push(`Default workflow "${value.defaultWorkflow}" does not exist.`)
  }
  if (value.defaultWorkflow === null && value.workflows.length > 0) {
    general.warnings.push('No default workflow is set; tasks must name a workflow explicitly.')
  }

  errors.push(...general.errors)
  warnings.push(...general.warnings)
  return { errors, warnings, byWorkflow, general }
}

/** `WorkflowStateMigration` (types/workflow.ts): state name → state name maps. */
export const WorkflowStateMigrationSchema = z.object({
  renames: z.record(z.string().min(1), z.string().min(1)).optional(),
  reassign: z.record(z.string().min(1), z.string().min(1)).optional(),
})

/** `WorkflowMigrations`: per workflow key. */
export const WorkflowMigrationsSchema = z.record(z.string().min(1), WorkflowStateMigrationSchema)

/** Body of PUT /projects/:id/workflows (`WorkflowsUpdateRequest`). */
export const WorkflowsUpdateRequestSchema = ProjectWorkflowsSchema.extend({
  expectedPolicyVersion: z.number().int().nonnegative().nullable().optional(),
  migrations: WorkflowMigrationsSchema.optional(),
})

/** Body of POST /workflow-templates (`CreateWorkflowTemplateRequest`). */
export const CreateWorkflowTemplateRequestSchema = z.object({
  workspaceId: z.string().uuid(),
  name: z.string().trim().min(1).max(100),
  description: z.string().max(2000).nullable().optional(),
  key: WorkflowKeySchema.optional(),
  definition: WorkflowDefinitionSchema,
})

/** Body of PUT /workflow-templates/:id (`UpdateWorkflowTemplateRequest`). */
export const UpdateWorkflowTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().max(2000).nullable().optional(),
  definition: WorkflowDefinitionSchema.optional(),
})

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

export const ProjectRepositorySchema = z.object({
  id: z.string(),
  url: z.string(),
  defaultBranch: z.string(),
  isDefault: z.boolean(),
})

export const ProjectWorkflowStateSchema = z.object({
  name: z.string(),
  agentRole: z.string().nullable(),
  requires: z.array(z.string()),
  blocks: z.array(z.string()),
  freshContext: z.boolean().optional(),
})

export const ProjectPolicySchema = z.object({
  project: z.string(),
  version: z.number().int().positive(),
  /** @deprecated flattened view of `workflows` — see types/project.ts. */
  workflow: z.array(ProjectWorkflowStateSchema),
  workflows: z.array(WorkflowDefinitionSchema),
  defaultWorkflow: z.string().nullable(),
  humanApproval: z.array(z.string()),
  forbidden: z.array(z.string()),
})

export const ProjectSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  key: z.string().regex(PROJECT_KEY_RE),
  name: z.string().min(1),
  description: z.string().nullable(),
  repositories: z.array(ProjectRepositorySchema),
  documentationSources: z.array(z.string()),
  architectureRules: z.string().nullable(),
  securityRules: z.string().nullable(),
  codingConventions: z.string().nullable(),
  specMode: z.enum(SPEC_MODES),
  policy: ProjectPolicySchema,
  definitionOfReady: z.array(z.string()),
  definitionOfDone: z.array(z.string()),
  mcpServiceIds: z.array(z.string()),
  allowedAgentIds: z.array(z.string()),
  projectMemoryCollectionId: z.string().nullable(),
  repositoryAllowlist: z.array(z.string()),
  networkAllowlist: z.array(z.string()),
  budgets: BudgetLimitsSchema,
  retentionPolicy: RetentionPolicySchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Agent
// ---------------------------------------------------------------------------

export const AgentTriggerSchema = z.object({
  id: z.string(),
  kind: z.enum([
    'manual',
    'schedule',
    'plane_webhook',
    'github_webhook',
    'api_call',
    'run_follow_up',
  ]),
  config: z.record(z.unknown()),
  isEnabled: z.boolean(),
})

export const AgentSkillOverrideSchema = z.object({
  skillId: z.string(),
  /** A pinned version of the skill, or 'latest' (unpinned). */
  skillVersion: z.string().min(1),
  enabled: z.boolean(),
})

export const AgentLastResultSummarySchema = z.object({
  runId: z.string(),
  status: z.nativeEnum(RunStatus),
  finishedAt: IsoDateTimeSchema,
})

export const AgentSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string().min(1),
  roleId: z.string(),
  roleVersion: z.number().int().positive(),
  projectId: z.string(),
  executorId: z.string(),
  runner: z.string().nullable().optional(),
  model: z.string(),
  additionalInstruction: z.string().nullable(),
  /** Partial run settings merged over the role's (agent wins); null = the role's as they are. */
  runSettingsOverride: RunSettingsOverrideSchema.nullable().optional(),
  skillOverrides: z.array(AgentSkillOverrideSchema),
  mcpServiceIds: z.array(z.string()),
  sandboxProfileId: z.string(),
  budgetLimits: BudgetLimitsSchema,
  triggers: z.array(AgentTriggerSchema),
  isActive: z.boolean(),
  currentRunId: z.string().nullable(),
  lastResult: AgentLastResultSummarySchema.nullable(),
  successRatePercent: z.number().min(0).max(100).nullable(),
  avgCostCents: z.number().nonnegative().nullable(),
  /** Whether every one of the agent's cost-bearing runs was a subscription login (nothing actually billed) — see Run.billing. */
  avgCostAllSubscription: z.boolean().optional(),
  avgDurationMs: z.number().nonnegative().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Task
// ---------------------------------------------------------------------------

export const TaskExternalReferenceSchema = z.object({
  system: z.enum(['plane', 'github', 'other']),
  externalId: z.string(),
  url: z.string().nullable(),
})

export const TaskSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  projectId: z.string(),
  title: z.string().min(1),
  instruction: z.string(),
  source: z.nativeEnum(TaskSource),
  sourceDedupeKey: z.string().nullable(),
  desiredAgentId: z.string().nullable(),
  desiredRoleId: z.string().nullable(),
  externalReferences: z.array(TaskExternalReferenceSchema),
  priority: z.number().int(),
  acceptanceCriteria: z.array(z.string()),
  riskLevel: z.nativeEnum(RiskLevel),
  maxBudgetCents: z.number().int().nonnegative().nullable(),
  maxDurationMs: z.number().int().nonnegative().nullable(),
  desiredSandboxProfileId: z.string().nullable(),
  inputFileRefs: z.array(z.string()),
  requiredApprovalTypes: z.array(z.string()),
  status: z.enum([
    'pending',
    'validating',
    'assigned',
    'in_progress',
    'awaiting_approval',
    'rejected',
    'completed',
  ]),
  workflowKey: z.string().nullable().optional(),
  workflowState: z.string().nullable().optional(),
  taskType: z.string().nullable().optional(),
  workBranch: z.string().nullable().optional(),
  queueLane: z.enum(['inbox', 'backlog', 'ready']).nullable().optional(),
  rank: z.string().nullable().optional(),
  severity: z.enum(['S1', 'S2', 'S3', 'S4']).nullable().optional(),
  ticketNo: z.number().int().nullable().optional(),
  ticketKey: z.string().nullable().optional(),
  streamKey: z.string().nullable().optional(),
  laneEnteredAt: IsoDateTimeSchema.nullable().optional(),
  archivedAt: IsoDateTimeSchema.nullable().optional(),
  queueReason: z.string().nullable().optional(),
  customVars: z.record(z.union([z.string(), z.number(), z.boolean()])).optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Sandbox profile
// ---------------------------------------------------------------------------

export const ResourceLimitsSchema = z.object({
  cpuCores: z.number().positive().optional(),
  memoryMb: z.number().int().positive().optional(),
  storageMb: z.number().int().positive().optional(),
  maxPids: z.number().int().positive().optional(),
  maxDurationMs: z.number().int().positive().optional(),
})

export const SandboxMountSchema = z.object({
  hostPath: z.string(),
  containerPath: z.string(),
  readOnly: z.boolean(),
})

export const SandboxProfileSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string().min(1),
  type: z.nativeEnum(SandboxProfileType),
  resourceLimits: ResourceLimitsSchema,
  readOnlyRootFilesystem: z.boolean(),
  allowedMounts: z.array(SandboxMountSchema),
  networkAllowlist: z.array(z.string()),
  nonRootUser: z.boolean(),
  imageRef: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Executor profile
// ---------------------------------------------------------------------------

export const ExecutorProfileSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  name: z.string().min(1),
  type: z.nativeEnum(ExecutorType),
  cliOrApiVersion: z.string(),
  defaultModel: z.string(),
  defaultModelParameters: z.record(z.unknown()),
  credentialProfileId: z.string().nullable(),
  maxDurationMs: z.number().int().positive(),
  maxTokens: z.number().int().positive().nullable(),
  maxCostCents: z.number().int().positive().nullable(),
  supportedFeatures: z.array(
    z.enum(['streaming', 'tool_use', 'file_edit', 'git_operations', 'vision'])
  ),
  healthStatus: z.string(),
  lastHealthCheckAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Run
// ---------------------------------------------------------------------------

export const RunSchema = z.object({
  id: z.string(),
  taskId: z.string(),
  agentId: z.string(),
  roleId: z.string(),
  roleVersion: z.number().int().positive(),
  projectId: z.string(),
  executorProfileId: z.string(),
  model: z.string(),
  sandboxProfileId: z.string(),
  status: z.nativeEnum(RunStatus),
  configSnapshot: z.record(z.unknown()),
  contextSnapshot: z.record(z.unknown()),
  contextHash: z.string(),
  contextVersion: z.string().nullable(),
  contextMode: z.enum(['goblic', 'native']).nullable(),
  queuedAt: IsoDateTimeSchema,
  startedAt: IsoDateTimeSchema.nullable(),
  endedAt: IsoDateTimeSchema.nullable(),
  tokensUsed: z.number().int().nonnegative(),
  costCents: z.number().nonnegative(),
  /** 'subscription' when the run's connection was a subscription login (nothing billed per run); null for a run from before this was tracked. */
  billing: z.enum(['subscription', 'api']).nullable().optional(),
  // `result` is validated with RunResultSchema (imported above) when present.
  result: z.unknown().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Approval
// ---------------------------------------------------------------------------

// TODO: model ApprovalAction as a proper zod discriminated union mirroring
// types/approval.ts once the exact per-kind payloads are finalized; a loose
// shape is enough for the scaffold so callers can still validate the
// envelope fields (runId + kind) without over-constraining action payloads.
export const ApprovalActionSchema = z
  .object({
    kind: z.enum([
      'budget_or_time_exceeded',
      'privileged_or_destructive_tool',
      'new_network_domain',
      'branch_push',
      'pull_request_create_or_ready',
      'follow_up_agent',
      // 'persistent_memory_save' is retired (memory → knowledge base, migration 0019).
      'deployment_merge_or_release',
      'agent_question',
      'agent_request',
    ]),
    runId: z.string(),
  })
  .passthrough()

/**
 * `workflow_gate` actions are created by the workflow engine only, never
 * requested by a run — so they are deliberately not part of
 * `ApprovalActionSchema` (which validates worker-requested approvals).
 */
export const WorkflowGateActionSchema = z.object({
  kind: z.literal('workflow_gate'),
  taskId: z.string(),
  workflowKey: z.string(),
  state: z.string(),
  onApprove: z.string().nullable(),
  onReject: z.string().nullable(),
  instructions: z.string().nullable().optional(),
  variables: z.record(z.string(), z.object({ value: z.string(), reason: z.string().nullable() })).optional(),
})

/** A historical `persistent_memory_save` approval (retired in migration 0019) — display only. */
export const LegacyPersistentMemorySaveActionSchema = z
  .object({
    kind: z.literal('persistent_memory_save'),
    runId: z.string(),
  })
  .passthrough()

export const ApprovalSchema = z.object({
  id: z.string(),
  runId: z.string().nullable(),
  taskId: z.string().nullable().optional(),
  type: z.nativeEnum(ApprovalType),
  action: z.union([ApprovalActionSchema, WorkflowGateActionSchema, LegacyPersistentMemorySaveActionSchema]),
  actionHash: z.string(),
  reason: z.string(),
  agentId: z.string().nullable(),
  projectId: z.string(),
  riskIndicator: z.enum(['low', 'medium', 'high', 'critical']),
  expectedCostCents: z.number().nonnegative().nullable(),
  status: z.nativeEnum(ApprovalStatus),
  expiresAt: IsoDateTimeSchema,
  resolvedBy: z.string().nullable(),
  resolvedAt: IsoDateTimeSchema.nullable(),
  additionalInstruction: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// Audit log entry
// ---------------------------------------------------------------------------

export const AuditLogEntrySchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  /** Usually one of AuditEventType; routes and services also record their own dotted names. */
  eventType: z.string(),
  actorUserId: z.string().nullable(),
  /** Display name of the acting user, when there is one. */
  actorName: z.string().nullable().optional(),
  /** Who acted when it wasn't a user (e.g. the worker). */
  actorLabel: z.string().nullable().optional(),
  /** One readable sentence, e.g. "Revoked credential". */
  summary: z.string().optional(),
  targetType: z.string(),
  targetId: z.string(),
  metadata: z.record(z.unknown()),
  createdAt: IsoDateTimeSchema,
})

// ---------------------------------------------------------------------------
// "Create" input schemas — validate caller-supplied payloads for the
// entities that are created through an API route. These intentionally omit
// server-generated fields (id, status, timestamps, computed averages, ...);
// optional fields fall back to the same defaults the API/flow engine would
// otherwise apply.
// ---------------------------------------------------------------------------

export const TaskCreateSchema = z.object({
  workspaceId: z.string(),
  projectId: z.string(),
  title: z.string().min(1),
  instruction: z.string(),
  source: z.nativeEnum(TaskSource),
  sourceDedupeKey: z.string().nullable().optional(),
  desiredAgentId: z.string().nullable().optional(),
  desiredRoleId: z.string().nullable().optional(),
  externalReferences: z.array(TaskExternalReferenceSchema).optional(),
  priority: z.number().int().optional(),
  acceptanceCriteria: z.array(z.string()).optional(),
  riskLevel: z.nativeEnum(RiskLevel),
  maxBudgetCents: z.number().int().nonnegative().nullable().optional(),
  maxDurationMs: z.number().int().nonnegative().nullable().optional(),
  desiredSandboxProfileId: z.string().nullable().optional(),
  inputFileRefs: z.array(z.string()).optional(),
  requiredApprovalTypes: z.array(z.string()).optional(),
})

export type TaskCreate = z.infer<typeof TaskCreateSchema>

export const AgentCreateSchema = z.object({
  workspaceId: z.string(),
  name: z.string().min(1),
  roleId: z.string(),
  projectId: z.string(),
  executorId: z.string(),
  runner: z.string().nullable().optional(),
  model: z.string(),
  additionalInstruction: z.string().nullable().optional(),
  skillOverrides: z.array(AgentSkillOverrideSchema).optional(),
  mcpServiceIds: z.array(z.string()).optional(),
  sandboxProfileId: z.string(),
  budgetLimits: BudgetLimitsSchema.optional(),
  triggers: z.array(AgentTriggerSchema).optional(),
})

export type AgentCreate = z.infer<typeof AgentCreateSchema>

/**
 * Input to start a run for an already-validated (task, agent) pair (FD
 * §17.2 step 1-2). Everything else — role/executor/sandbox versions, the
 * config/context snapshot, budgets — is resolved server-side from the
 * five-layer configuration (FD §14.2), not supplied by the caller.
 */
export const RunCreateSchema = z.object({
  taskId: z.string(),
  agentId: z.string(),
})

export type RunCreate = z.infer<typeof RunCreateSchema>

export const WorkspaceCreateSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  defaultTimezone: z.string(),
  defaultBudgets: BudgetLimitsSchema.optional(),
  retentionPolicy: RetentionPolicySchema.optional(),
  allowedExecutorTypes: z.array(z.nativeEnum(ExecutorType)),
  defaultNetworkPolicy: NetworkPolicySchema,
  defaultApprovalPolicy: ApprovalPolicySchema,
})

export type WorkspaceCreate = z.infer<typeof WorkspaceCreateSchema>

// ---------------------------------------------------------------------------
// RunResult validation entry point
// ---------------------------------------------------------------------------

/**
 * Throwing parse of a RunResult (FD §17.3) — same semantics as
 * `parseRunResult`, exposed under this name for callers that validate
 * against a named `validate<Entity>` convention. Throws a ZodError on an
 * invalid payload.
 */
export function validateRunResult(data: unknown): RunResult {
  return RunResultSchema.parse(data)
}


export * from './subtasks.js'
